import type {
  PartnerCompaniesQueryParams,
  PaginatedPartnerCompanyList,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils/logging';

import {
  zodPartnerCompaniesQueryParamsSchema,
  zodPaginatedPartnerCompanyListSchema,
} from './validations';

/**
 * Fetches the paginated list of partner companies from the API.
 *
 * @param queryParams - Optional filter and pagination parameters.
 * @param options - Optional request options (e.g. abort signal).
 * @returns The paginated list of partner companies.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/get_partner_companies_data_exchange_companies_get
 */
export const fetchPartnerCompanies = async (
  queryParams: PartnerCompaniesQueryParams = {},
  options: KyRequestOptions,
): Promise<PaginatedPartnerCompanyList> => {
  const response = await vestigasApi
    .get(ENDPOINT.DATA_EXCHANGE.GET_DATA_EXCHANGE_COMPANIES(), {
      searchParams: queryParams,
      validate: {
        searchParams: zodPartnerCompaniesQueryParamsSchema.transform((data) =>
          withoutObjectKeysWhereValueIs(data, [undefined]),
        ),
      },
      ...options,
    })
    .json<PaginatedPartnerCompanyList>();

  const validatedResponse =
    zodPaginatedPartnerCompanyListSchema.safeParse(response);

  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Partner companies API response has validation errors',
      error: {
        errors: validatedResponse.error.issues,
        errorMessage: validatedResponse.error.message,
        response,
      },
    });
  }

  return response;
};
